import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import FormDate from "../../../../Components/v2/Form/FormDate";
import AccountingWrapper from "../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../Components/v2/Accounting/AccountingPageHeader";
import Table from "../../../../Components/v2/Table";
import TableActions from "../../../../Components/v2/Table/TableActions";
import Stats from "../../../../Components/v2/Stats";
import Pagination from "../../../../Components/v2/Pagination";
import FormSelect from "../../../../Components/v2/Form/FormSelect";
import Badge from "../../../../Components/Badge";
import { ModalAlert } from "../../../../Components/ModalAlert";
import AttendanceLogModal from "../../../../Components/AttendanceLogModal";
import {
  addLocalNotification,
  clearBusinessCalendar,
  clearBusinessEmployee,
  clearEmployeeAttendance,
  resetAttendanceViewData,
  requestDeleteEmployeeAttendance,
  requestGetBusinessEmployee,
  requestListBusinessCalendar,
  requestListBusinessEmployee,
  requestListEmployeeAttendance,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useNotificationDispatch,
} from "../../../../Context";
import {
  cancelAPIRequest,
  formatTime,
  generateMonthDates,
} from "../../../../Utils";
import { ATTENDANCE_STATUS_OPTIONS } from "../../../../Constants/business";
import { InformationCircleIcon } from "@heroicons/react/outline";
import FormError from "../../../../Components/v2/Form/FormError";
import ErrorDialog from "../../../../Components/v2/ErrorDialog";

const EmployeeAttendancesV2 = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const monthParam = searchParams.get("month");

  const dispatch = useBusinessTransactionDispatch();
  const notificationDispatch = useNotificationDispatch();
  const {
    request,
    employees,
    employeesLoading,
    employeesError,
    attendances,
    attendancesLoading,
    attendancesError,
    deletedAttendanceId,
    deleteAttendanceLoading,
    deleteAttendanceError,
    calendars,
    calendarsLoading,
    calendarsError,
    employee,
    getEmployeeLoading,
    getEmployeeError,
  } = useBusinessTransactionState();
  const { user } = useAuthState();

  const [selectedDate, setSelectedDate] = useState(() => {
    if (monthParam && dayjs(monthParam, "YYYY-MM", true).isValid()) {
      return dayjs(monthParam, "YYYY-MM").toDate();
    }
    return new Date();
  });
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [holidayMap, setHolidayMap] = useState({});
  const [attendanceLogModalOpen, setAttendanceLogModalOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const selectedMonth = dayjs(selectedDate).format("YYYY-MM");

  const monthDates = useMemo(
    () => generateMonthDates(selectedDate),
    [selectedDate],
  );

  // Clear stale data on mount and cleanup on unmount
  useEffect(() => {
    return () => {
      cancelAPIRequest(request);
      clearBusinessEmployee(dispatch);
      clearBusinessCalendar(dispatch);
      resetAttendanceViewData(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEmployees = () => {
    if (user?.mainBusiness?.id) {
      requestListBusinessEmployee(dispatch, {
        page: 1,
        limit: 100,
        businessID: user.mainBusiness.id,
      });
    }
  };

  // Load all employees for select dropdown
  useEffect(() => {
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user?.mainBusiness?.id]);

  // Load employee detail when id is in params
  useEffect(() => {
    if (id) {
      requestGetBusinessEmployee(dispatch, { id });
    }
  }, [dispatch, id]);

  // Sync selected employee state with URL param
  useEffect(() => {
    if (id && employees.length > 0) {
      const found = employees.find((e) => e.id === id);
      if (found) {
        setSelectedEmployee({
          value: found.id,
          label: `${found.firstName} ${found.lastName || ""}`,
        });
      }
    }
  }, [id, employees]);

  // Load attendances when employee id + month changes
  useEffect(() => {
    if (id) {
      requestListEmployeeAttendance(dispatch, {
        employeeID: id,
        attendanceDate: { beginsWith: selectedMonth },
      });
    }
  }, [dispatch, id, selectedMonth]);

  const loadCalendars = () => {
    if (user?.mainBusiness?.id) {
      requestListBusinessCalendar(dispatch, {
        businessID: user.mainBusiness.id,
        page: 1,
        limit: 10,
        holidayDateFilter: { beginsWith: selectedMonth },
      });
    }
  };

  // Load calendars for holiday/work week info
  useEffect(() => {
    loadCalendars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, user?.mainBusiness?.id, selectedMonth]);

  // Build holiday map
  useEffect(() => {
    if (calendars?.length > 0) {
      const map = {};
      calendars.forEach((calendar) => {
        calendar.holidays?.items?.forEach((h) => {
          map[h.date] = h;
        });
      });
      setHolidayMap(map);
    } else {
      setHolidayMap({});
    }
  }, [calendars]);

  // Sync month param from URL
  useEffect(() => {
    if (monthParam && dayjs(monthParam, "YYYY-MM", true).isValid()) {
      const fromParam = dayjs(monthParam, "YYYY-MM").toDate();
      if (monthParam !== dayjs(selectedDate).format("YYYY-MM")) {
        setSelectedDate(fromParam);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthParam]);

  // Handle delete success
  useEffect(() => {
    if (deletedAttendanceId) {
      setShowConfirmModal(false);
      addLocalNotification(notificationDispatch, {
        id: `delete-attendance-${deletedAttendanceId}`,
        type: "success",
        title: "common.success",
        body: "employees.attendances.notifications.deleteSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, deletedAttendanceId]);

  useEffect(() => {
    if (deleteAttendanceError) {
      setShowConfirmModal(false);
      setShowErrorModal(true);
    }
  }, [deleteAttendanceError]);

  const getAttendanceForDate = (date) =>
    attendances.find((a) => a.attendanceDate === date);

  const isWorkingDay = (date) => {
    const holiday = holidayMap[date];
    if (holiday) return false;

    if (calendars?.length > 0) {
      const dayOfWeek = dayjs(date).day();
      const dayKey = dayOfWeek.toString();
      for (const calendar of calendars) {
        if (calendar.workWeekPattern) {
          const pattern = JSON.parse(calendar.workWeekPattern);
          return pattern[dayKey] !== false;
        }
      }
    }
    const dayOfWeek = dayjs(date).day();
    return dayOfWeek >= 1 && dayOfWeek <= 5;
  };

  const isLateAttendance = (attendance) => {
    if (!attendance?.clockInTime || !employee?.standardWorkStartTime)
      return false;
    return dayjs(`2000-01-01 ${attendance.clockInTime}`).isAfter(
      dayjs(`2000-01-01 ${employee.standardWorkStartTime}`),
    );
  };

  const getDateTextClass = (date) => {
    const holiday = holidayMap[date];
    if (holiday) return holiday.isPaid ? "text-red-500" : "text-yellow-500";
    if (calendars?.length > 0) {
      const dayOfWeek = dayjs(date).day();
      const dayKey = dayOfWeek.toString();
      for (const calendar of calendars) {
        if (calendar.workWeekPattern) {
          const pattern = JSON.parse(calendar.workWeekPattern);
          if (pattern[dayKey] === false) return "text-red-500";
        }
      }
    }
    return "text-gray-700";
  };

  const getStatusLabel = (statusValue) => {
    const found = ATTENDANCE_STATUS_OPTIONS.find(
      (o) => o.value === statusValue,
    );
    return found ? found.label : statusValue;
  };

  const getStatusBadgeType = (status) => {
    switch (status) {
      case "PRESENT":
        return "success";
      case "ABSENT":
        return "danger";
      case "SICK_LEAVE":
      case "MEDICAL_LEAVE":
      case "MATERNITY_LEAVE":
      case "PATERNITY_LEAVE":
      case "COMPASSIONATE_LEAVE":
        return "alert";
      case "ANNUAL_LEAVE":
      case "PERSONAL_LEAVE":
      case "EMERGENCY_LEAVE":
      case "UNPAID_LEAVE":
        return "secondary";
      case "REMOTE_WORK":
      case "BUSINESS_TRIP":
      case "TRAINING":
      case "CONFERENCE":
      case "CLIENT_MEETING":
      case "COMPANY_EVENT":
        return "info";
      default:
        return "primary";
    }
  };

  const calculateStats = () => {
    const totalDaysInMonth = monthDates.length;
    const workingDays = monthDates.filter((d) => isWorkingDay(d)).length;
    let absenceDays = 0;
    monthDates.forEach((date) => {
      if (isWorkingDay(date)) {
        const a = getAttendanceForDate(date);
        if (!a || a.status === "ABSENT" || a.status === "UNPAID_LEAVE")
          absenceDays++;
      }
    });
    const lateDays = attendances.filter((a) => isLateAttendance(a)).length;
    const absencePercentage =
      workingDays > 0 ? Math.round((absenceDays / workingDays) * 100) : 0;
    return {
      totalWorkingDays: workingDays,
      totalDaysInMonth,
      absenceDays,
      absencePercentage,
      lateDays,
    };
  };

  const handleEmployeeChange = (option) => {
    setSelectedEmployee(option);
    if (option?.value) {
      navigate(
        `/v2/accounting/employees/attendance/${option.value}?month=${selectedMonth}`,
      );
    } else {
      navigate(`/v2/accounting/employees/attendance`);
    }
  };

  const handleMonthChange = (date) => {
    setSelectedDate(date);
    const newMonth = dayjs(date).format("YYYY-MM");
    if (id) {
      setSearchParams({ month: newMonth });
    }
  };

  const handleEdit = (attendance) => {
    setSelectedAttendance(attendance);
    setAttendanceLogModalOpen(true);
  };

  const handleDelete = (attendance) => {
    setSelectedAttendance(attendance);
    setShowConfirmModal(true);
  };

  const handleClose = () => {
    cancelAPIRequest(request);
    setAttendanceLogModalOpen(false);
    setSelectedAttendance(null);
    clearEmployeeAttendance(dispatch);
  };

  const onDelete = () => {
    requestDeleteEmployeeAttendance(dispatch, { id: selectedAttendance?.id });
  };

  const stats = calculateStats();

  // No employee, or a failed attendance/calendar load, leaves nothing to count:
  // every day would read as an absence. Show dashes instead.
  const statsUnavailable = !id || attendancesError || calendarsError;
  const attendanceStats = [
    {
      title: t("employees.attendances.stats.totalWorkingDays"),
      value: statsUnavailable ? "–" : stats.totalWorkingDays,
      footer: statsUnavailable
        ? undefined
        : t("employees.attendances.stats.fromDays", {
            count: stats.totalDaysInMonth,
          }),
    },
    {
      title: t("employees.attendances.stats.absenceDays"),
      value: statsUnavailable ? "–" : stats.absenceDays,
      footer: statsUnavailable ? undefined : `${stats.absencePercentage}%`,
    },
    {
      title: t("employees.attendances.stats.lateDays"),
      value: statsUnavailable ? "–" : stats.lateDays,
    },
  ];

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => ({
        value: e.id,
        label: `${e.firstName} ${e.lastName || ""}${e.employeeID ? ` (${e.employeeID})` : ""}`,
      })),
    [employees],
  );

  const isLoading =
    attendancesLoading || calendarsLoading || getEmployeeLoading;

  const tableData = useMemo(
    () =>
      monthDates.map((date) => {
        const attendance = getAttendanceForDate(date);
        return {
          date,
          attendance,
          textColorClass: getDateTextClass(date),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monthDates, attendances, holidayMap, calendars],
  );

  // Reset to page 1 whenever the full dataset or perPage changes
  useEffect(() => {
    setPage(1);
  }, [tableData, perPage]);

  const pagedTableData = useMemo(() => {
    const start = (page - 1) * perPage;
    return tableData.slice(start, start + perPage);
  }, [tableData, page, perPage]);

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        <AccountingPageHeader title="employees.attendances.title" />

        <div className="px-4 py-5 sm:px-6 lg:px-8">
          <Stats
            stats={attendanceStats}
            showCurrency={false}
            isLoading={isLoading && Boolean(id)}
          />
        </div>

        <div className="px-4 pb-4 sm:px-6 lg:px-8">
          <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormSelect
              searchable
              isLoading={employeesLoading}
              placeholder="employees.attendances.filter.selectEmployee"
              options={employeeOptions}
              value={selectedEmployee}
              onChange={handleEmployeeChange}
              onClear={() => handleEmployeeChange(null)}
            />

            <FormDate
              selected={selectedDate}
              onChange={handleMonthChange}
              showMonthYearPicker
              dateFormat="MMM yyyy"
              disabled={!id}
              placeholderText={t("employees.attendances.filter.selectMonth")}
            />
          </div>

          <FormError
            className="mb-4"
            error={employeesError}
            title="employees.attendances.errors.employees"
            onRetry={loadEmployees}
          />
          <FormError
            className="mb-4"
            error={calendarsError}
            title="employees.attendances.errors.calendars"
            onRetry={loadCalendars}
          />

          {!id && (
            <div className="mt-4 flex items-start gap-3 rounded-lg bg-blue-50 border border-blue-200 px-4 py-4">
              <InformationCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-700">
                {t("employees.attendances.selectEmployeePrompt")}
              </p>
            </div>
          )}

          {id && (
            <FormError
              className="mt-4"
              error={getEmployeeError}
              onRetry={() => requestGetBusinessEmployee(dispatch, { id })}
            />
          )}

          {/* Table */}
          {id && !getEmployeeError && (
            <Table
              isLoading={isLoading}
              error={attendancesError}
              data={pagedTableData}
              columns={[
                {
                  header: "employees.attendances.fields.date",
                  accessor: (row) => (
                    <span className={`whitespace-nowrap ${row.textColorClass}`}>
                      {dayjs(row.date)
                        .locale(i18n.language)
                        .format("D MMMM YYYY")}
                    </span>
                  ),
                },
                {
                  header: "employees.attendances.fields.clockIn",
                  accessor: (row) =>
                    row.attendance
                      ? formatTime(row.attendance.clockInTime)
                      : "-",
                },
                {
                  header: "employees.attendances.fields.clockOut",
                  accessor: (row) =>
                    row.attendance
                      ? formatTime(row.attendance.clockOutTime)
                      : "-",
                },
                {
                  header: "employees.attendances.fields.status",
                  accessor: (row) =>
                    row.attendance ? (
                      <Badge
                        type={getStatusBadgeType(row.attendance.status)}
                        title={getStatusLabel(row.attendance.status)}
                      />
                    ) : (
                      "-"
                    ),
                },
                {
                  header: "common.actions.label",
                  accessor: (row, index) => {
                    if (!row.attendance) {
                      const canAdd =
                        !employee?.endDate ||
                        dayjs(row.date).isSameOrBefore(dayjs(employee.endDate));
                      if (!canAdd) return null;
                      return (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedAttendance({ attendanceDate: row.date });
                            setAttendanceLogModalOpen(true);
                          }}
                          className="text-blue-500 hover:text-blue-700 text-sm"
                          title={t("employees.attendances.add")}
                        >
                          + {t("common.add")}
                        </button>
                      );
                    }
                    return (
                      <TableActions
                        position={
                          index === pagedTableData.length - 1 ? "top" : "bottom"
                        }
                        actions={[
                          {
                            label: t("common.edit"),
                            onClick: () => handleEdit(row.attendance),
                          },
                          {
                            label: t("common.delete"),
                            onClick: () => handleDelete(row.attendance),
                            variant: "danger",
                          },
                        ]}
                      />
                    );
                  },
                  align: "right",
                },
              ]}
            />
          )}
        </div>

        {/* Pagination */}
        {id && !getEmployeeError && (
          <Pagination
            isOffline
            items={tableData}
            currentPage={page}
            isLoading={isLoading}
            perPage={perPage}
            onPerPageChange={(newPerPage) => {
              setPerPage(newPerPage);
              setPage(1);
            }}
            onPageChange={(newPage) => setPage(newPage)}
          />
        )}

        {/* Attendance Log Modal */}
        {attendanceLogModalOpen && (
          <AttendanceLogModal
            open={attendanceLogModalOpen}
            onClose={handleClose}
            attendance={selectedAttendance}
            calendars={calendars}
            employee={employee}
          />
        )}

        {/* Delete Confirm Modal */}
        <ModalAlert
          open={showConfirmModal}
          setOpen={(open) => setShowConfirmModal(open)}
          title="employees.attendances.modal.title.delete"
          description="employees.attendances.modal.description.delete"
          confirmButtonText="common.yes"
          cancelButtonText="common.cancel"
          loadingButton={deleteAttendanceLoading}
          disabledButton={deleteAttendanceLoading}
          onConfirm={onDelete}
          onCancel={() => {
            cancelAPIRequest(request);
            setShowConfirmModal(false);
          }}
        />

        {/* Error Modal */}
        {deleteAttendanceError && (
          <ErrorDialog
            open={showErrorModal}
            error={deleteAttendanceError}
            onClose={() => {
              setShowErrorModal(false);
              clearEmployeeAttendance(dispatch);
            }}
          />
        )}
      </main>
    </AccountingWrapper>
  );
};

export default EmployeeAttendancesV2;
